import { Routes } from '@angular/router';
import { renovacionActiva } from '@core/routing/renovacion-activa.guard';
import { ORIGEN_DEL_PAGO } from '@features/pago/origen-del-pago';
import { PagoDeRenovacion } from '@features/renovacion/pago-de-renovacion';
import {
  RUTA_HORARIO,
  RUTA_INICIO,
  RUTA_MATRICULA,
  RUTA_PAGO,
  RUTA_REENTRANTE,
  RUTA_RENOVACION,
  RUTA_RETORNO_PAGO,
  RUTA_RETORNO_RENOVACION,
  RUTA_SEDE,
} from '@core/routing/rutas-matricula';

/**
 * Rutas de la aplicacion.
 *
 * Las rutas del flujo llegan con las specs que entregan cada pantalla: no se
 * declaran rutas hacia pantallas que todavia no existen (Regla 11 de
 * .ralph/PROMPT.md, y AC-13 de SPEC-003).
 */
export const routes: Routes = [
  {
    // La eleccion de tramite (SPEC-004). Vive en la raiz porque ahi vivia el
    // menu que reemplaza: los enlaces y los codigos QR al inicio siguen
    // sirviendo. Con ella desaparece la ruta de humo de SPEC-001.
    path: RUTA_INICIO,
    loadComponent: () => import('./features/inicio/inicio.component').then((m) => m.InicioComponent),
  },
  {
    // Matricula nueva (SPEC-005). El layout monta el caparazon de SPEC-003 y
    // cada micro-pantalla entra como ruta hija, con su nombre en `data` — que es
    // lo que lee la sincronia con el store.
    path: RUTA_MATRICULA,
    loadComponent: () =>
      import('./features/matricula-nueva/matricula-layout.component').then(
        (m) => m.MatriculaLayoutComponent,
      ),
    children: [
      // Un macro-paso, dos formas de URL, y las dos las contempla el contrato de
      // SPEC-003: con micro-pantalla es lo que se recorre en compacto; sin ella
      // es el macro-paso completo, que es lo que se ve en amplio. El numero va
      // literal y no como parametro: cada macro-paso registra la suya cuando su
      // spec lo entrega, y `3/foto` no resuelve a nada hasta que llegue SPEC-008.
      {
        path: '1/:pantalla',
        data: { macro: 1 },
        loadComponent: () =>
          import('./features/matricula-nueva/paso1/paso1.component').then((m) => m.Paso1Component),
      },
      {
        path: '1',
        data: { macro: 1 },
        loadComponent: () =>
          import('./features/matricula-nueva/paso1/paso1.component').then((m) => m.Paso1Component),
      },
      // El paso 2 (SPEC-006). `firma` pertenece a este macro-paso y la entrega
      // SPEC-007: hoy la ruta resuelve y el paso no pinta nada en ella. Se
      // registra igual porque `2/:pantalla` es una sola ruta, y porque negarle
      // la ruta dejaria al store apuntando a una posicion sin URL — que es como
      // el final del paso 1 expulsaba al inicio antes de esta spec.
      {
        path: '2/:pantalla',
        data: { macro: 2 },
        loadComponent: () =>
          import('./features/matricula-nueva/paso2/paso2.component').then((m) => m.Paso2Component),
      },
      {
        path: '2',
        data: { macro: 2 },
        loadComponent: () =>
          import('./features/matricula-nueva/paso2/paso2.component').then((m) => m.Paso2Component),
      },
      // El paso 3 (SPEC-008). Hasta aqui `/matricula/3` caia en el comodin y
      // devolvia al inicio: terminar el paso 2 expulsaba del tramite, que es el
      // mismo fallo que SPEC-006 dejo a medias y que costo SPEC-021 entera.
      // `MICRO_PANTALLAS[2]` y `posicionDeRuta` ya conocian `foto`; lo que
      // faltaba era esto.
      {
        path: '3/:pantalla',
        data: { macro: 3 },
        loadComponent: () =>
          import('./features/matricula-nueva/paso3/paso3.component').then((m) => m.Paso3Component),
      },
      {
        path: '3',
        data: { macro: 3 },
        loadComponent: () =>
          import('./features/matricula-nueva/paso3/paso3.component').then((m) => m.Paso3Component),
      },
      // El paso 4 (SPEC-009). Hasta aqui `/matricula/4` caia en el comodin y
      // devolvia al inicio: terminar el paso 3 expulsaba del tramite, que es el
      // mismo fallo que costo SPEC-021 entera y que SPEC-008 tuvo que arreglar
      // sobre la marcha. `MICRO_PANTALLAS[3]` y `posicionDeRuta` ya conocian
      // `revisar`; lo que faltaba era esto. Con el, **ya no queda ningun
      // macro-paso sin entregar**.
      {
        path: '4/:pantalla',
        data: { macro: 4 },
        loadComponent: () =>
          import('./features/matricula-nueva/paso4/paso4.component').then((m) => m.Paso4Component),
      },
      {
        path: '4',
        data: { macro: 4 },
        loadComponent: () =>
          import('./features/matricula-nueva/paso4/paso4.component').then((m) => m.Paso4Component),
      },
      { path: '', pathMatch: 'full', redirectTo: '1' },
    ],
  },
  {
    // El estado del tramite (SPEC-010). Es la ruta por la que llega el enlace
    // del correo y **no se cambia**: hay enlaces vivos apuntando ahi. Vive fuera
    // del caparazon del formulario a proposito — el envio llama a `limpiar()` al
    // aterrizar aqui, y dentro del caparazon una recarga devolveria al paso 1.
    path: RUTA_REENTRANTE,
    loadComponent: () =>
      import('./features/seguimiento/seguimiento.component').then((m) => m.SeguimientoComponent),
  },
  {
    // La eleccion de sede (SPEC-011). Cuelga del id de la solicitud y **no** es
    // hija de la ruta de arriba: aquella es una hoja y Angular no le pasa
    // segmentos de sobra.
    path: RUTA_SEDE,
    loadComponent: () =>
      import('./features/registro/sede.component').then((m) => m.SedeComponent),
  },
  {
    // La eleccion de horario (SPEC-011). El ciclo y la sede van en la direccion
    // porque los grupos se consultan por uno y se filtran por la otra: una
    // recarga sin ellos no sabria que ensenar.
    path: RUTA_HORARIO,
    loadComponent: () =>
      import('./features/registro/horario.component').then((m) => m.HorarioComponent),
  },
  {
    // El resumen de pago (SPEC-012). Solo el id: para cuando se llega aqui la
    // eleccion ya esta guardada en la solicitud.
    path: RUTA_PAGO,
    loadComponent: () =>
      import('./features/registro/pago.component').then((m) => m.PagoComponent),
  },
  {
    // El retorno de la pasarela (SPEC-013). **Hasta aqui caia en el comodin y
    // acababa en el inicio**: es a donde PayU devuelve —`responseUrl`, que
    // compone `buildPaymentFlowControl`— y a donde enlaza el informe de
    // legalizaciones del portal de gestion. Las dos entradas se perdian igual.
    //
    // Cuelga del id de la solicitud, como el resto de la continuidad: el
    // `paymentId` no viaja en la direccion y se lee de la solicitud.
    path: RUTA_RETORNO_PAGO,
    loadComponent: () =>
      import('./features/pago/retorno.component').then((m) => m.RetornoComponent),
  },
  {
    // La renovacion (SPEC-015). **Hasta aqui caia en el comodin**: la tarjeta
    // "Ya estudio aquí" del inicio apunta a esta direccion desde SPEC-004 y se
    // perdia en el camino.
    //
    // Lleva el primer guard del proyecto: apagar la tarjeta del inicio no basta
    // porque la direccion escrita a mano —o un enlace guardado del ciclo pasado—
    // entraba igual, y con el modulo cerrado la renovacion es presencial.
    // **Y con SPEC-016, el recorrido entero**: buscar, la ficha, y de ahi a
    // sede, horario y pago —que son las pantallas del bloque 6, no una copia—.
    // Cuelgan de aqui para que el guard las cubra a todas, y se cargan tarde
    // para no dejar la renovacion en el paquete de quien viene a matricularse
    // por primera vez.
    path: RUTA_RENOVACION,
    canActivate: [renovacionActiva],
    loadChildren: () =>
      import('./features/renovacion/renovacion.routes').then((m) => m.RUTAS_RENOVACION),
  },
  {
    // El retorno de la pasarela de una renovacion (SPEC-016, AC-5). **Hasta
    // aqui caia en el comodin y acababa en el inicio**: es la direccion que
    // compone `buildPaymentFlowControl` con el origen
    // `renewal-registration`, que el dominio del pago ya declaraba y que no
    // atendia nadie.
    //
    // Su `:id` no es el de la membresia: es el `uuid` con el que se creo el
    // pago, y que viaja tambien en `administrativeData.applicationId`.
    //
    // Es la misma pantalla que la vuelta de una matricula nueva: la espera y la
    // tabla de estados no cambian. Lo que cambia es de donde sale el pago, que
    // no se anota nada —de eso se encarga la Logic App— y a donde se sale.
    path: RUTA_RETORNO_RENOVACION,
    providers: [{ provide: ORIGEN_DEL_PAGO, useExisting: PagoDeRenovacion }],
    loadComponent: () =>
      import('./features/pago/retorno.component').then((m) => m.RetornoComponent),
  },
  {
    // Banco de pruebas del caparazon (SPEC-003). Vive bajo el catalogo por la
    // misma razon que el: es superficie interna de revision, no una pantalla
    // del flujo.
    path: 'ui-catalogo/caparazon',
    loadComponent: () =>
      import('./ui/catalogo/caparazon-demo/caparazon-demo.component').then(
        (m) => m.CaparazonDemoComponent,
      ),
  },
  {
    // Catalogo interno de la biblioteca `lip-*` (SPEC-002). No se enlaza desde
    // la navegacion publica y se carga aparte: no pesa en el paquete inicial.
    path: 'ui-catalogo',
    loadComponent: () => import('./ui/catalogo/catalogo.component').then((m) => m.CatalogoComponent),
  },
  { path: '**', redirectTo: '' },
];
